/**
 * medListRecordSync: a saved medication list offering its rows to the
 * client's Medications card.
 *
 * Saving a list linked to a client offers each of its rows to the record,
 * so a dose corrected on the list can reach the Medications card. The care
 * manager confirms each one before anything is written.
 *
 * What each row is to the record is the server's word (POST
 * .../ai-tools/med-list/record-match, the photo door's rules,
 * matchPhotoRowsToRecord):
 *   - on_record: the record add's twin (name, dose, directions, and the
 *     When the row is ticked for). Nothing to do, unless the list names
 *     another prescriber or reason, which this file reads off the record
 *     the page holds: then it offers "Update {X}" (state 'differs');
 *   - near: the same name with another dose or directions, or a look-alike
 *     name. "Did you mean {X}, already on the record?", Update {X} or Keep
 *     both, never picked for the person;
 *   - new: offered as an add.
 * A row marked stopped on the list, or with no name, is never offered:
 * stopping a medicine is done on the Medications card itself.
 *
 * Every write goes through the record's own add and edit routes
 * (postRecordRow, patchRecordRow), as the Medications card's form sends
 * them. A blank on the list leaves the record's value; medicines on the
 * record that the list leaves out are not touched.
 */

import { writeFetch } from './writeFetch';
import { ApiError } from './api';
import type { MedicationRow } from './clientRecord';
import type { MedListItem } from './medList';
import type { PhotoMedicationRecordMatch, PhotoRecordMedication } from './photoIntakeApi';

/** One row as the record check takes it: the list's directions are the
 *  record's directions (schedule). */
export interface MedListMatchRow {
  name: string;
  dose: string | null;
  schedule: string | null;
  times_of_day: string[] | null;
}

/** The rows a list offers the record: named, and not marked stopped. */
export function offerableItems(items: readonly MedListItem[]): MedListItem[] {
  return items.filter((i) => i.name.trim() !== '' && i.status !== 'discontinued');
}

const blankToNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

export function matchRowOf(item: MedListItem): MedListMatchRow {
  return {
    name: item.name.trim(),
    dose: blankToNull(item.dose),
    schedule: blankToNull(item.directions),
    times_of_day: item.times.length > 0 ? [...item.times] : null,
  };
}

/** Ask the server what each row is to the record, in the rows' order;
 *  null when this seat may not open the client's record. */
export async function matchMedListToRecord(
  slug: string,
  clientId: string,
  items: readonly MedListItem[],
): Promise<PhotoMedicationRecordMatch[] | null> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/ai-tools/med-list/record-match`;
  const res = await writeFetch(path, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_id: clientId, medications: items.map(matchRowOf) }),
    redirect: 'manual',
  });
  if (!res.ok) {
    let errBody: unknown;
    try {
      errBody = await res.json();
    } catch {
      errBody = null;
    }
    throw new ApiError(res.status, errBody, `POST ${path} → HTTP ${res.status}`);
  }
  const body = (await res.json()) as { medications: PhotoMedicationRecordMatch[] | null };
  return body.medications;
}

/** A medication on the record, as the offer reads it: the page's record
 *  row when it holds one, else the check's own few fields. */
export type RecordTarget = Pick<
  MedicationRow,
  'id' | 'name' | 'dose' | 'schedule' | 'prescriber' | 'prescriberId' | 'reason' | 'timesOfDay'
>;

export type MedListRecordOffer =
  | { state: 'on_record'; item: MedListItem; target: RecordTarget }
  | { state: 'differs'; item: MedListItem; target: RecordTarget; changes: MedListRecordField[] }
  | { state: 'near'; item: MedListItem; targets: RecordTarget[] }
  | { state: 'new'; item: MedListItem };

/** The record fields an update can change, in the record form's order. */
export type MedListRecordField = 'dose' | 'times_of_day' | 'schedule' | 'prescriber' | 'reason';

const same = (a: string | null | undefined, b: string | null | undefined): boolean =>
  (a ?? '').trim().replace(/\s+/g, ' ').toLowerCase() ===
  (b ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

function targetOf(row: PhotoRecordMedication, record: readonly MedicationRow[]): RecordTarget {
  const held = record.find((m) => m.id === row.id);
  if (held !== undefined) return held;
  return {
    id: row.id,
    name: row.name,
    dose: row.dose ?? undefined,
    schedule: row.schedule ?? undefined,
  };
}

/** The fields an update would change on `target`: a value the list holds
 *  that the record does not. A blank on the list changes nothing. */
export function changesFor(item: MedListItem, target: RecordTarget): MedListRecordField[] {
  const out: MedListRecordField[] = [];
  if (item.dose.trim() !== '' && !same(item.dose, target.dose)) out.push('dose');
  const recordTimes = target.timesOfDay ?? [];
  if (
    item.times.length > 0 &&
    (item.times.length !== recordTimes.length || item.times.some((t) => !recordTimes.includes(t)))
  ) {
    out.push('times_of_day');
  }
  if (item.directions.trim() !== '' && !same(item.directions, target.schedule))
    out.push('schedule');
  if (item.prescriber.trim() !== '' && !same(item.prescriber, target.prescriber))
    out.push('prescriber');
  if (item.reason.trim() !== '' && !same(item.reason, target.reason)) out.push('reason');
  return out;
}

/** Each offered row with what it is to the record. `matches` is the
 *  server's answer for offerableItems(items), in that order. */
export function recordOffers(
  items: readonly MedListItem[],
  matches: readonly PhotoMedicationRecordMatch[],
  record: readonly MedicationRow[],
): MedListRecordOffer[] {
  return offerableItems(items).map((item, i): MedListRecordOffer => {
    const m = matches[i];
    if (m === undefined || m.state === 'new') return { state: 'new', item };
    if (m.state === 'near') {
      return m.rows.length > 0
        ? { state: 'near', item, targets: m.rows.map((r) => targetOf(r, record)) }
        : { state: 'new', item };
    }
    const target = targetOf(m.row, record);
    const changes = changesFor(item, target);
    return changes.length > 0
      ? { state: 'differs', item, target, changes }
      : { state: 'on_record', item, target };
  });
}

/** The record add's `fields` for a row: the record form's fields the list
 *  holds, non-empty only. The list's Method has no place on the record. */
export function addFieldsFor(item: MedListItem): Record<string, unknown> {
  const out: Record<string, unknown> = { name: item.name.trim() };
  if (item.dose.trim() !== '') out['dose'] = item.dose.trim();
  if (item.times.length > 0) out['times_of_day'] = [...item.times];
  if (item.directions.trim() !== '') out['schedule'] = item.directions.trim();
  if (item.prescriber.trim() !== '') out['prescriber'] = item.prescriber.trim();
  if (item.reason.trim() !== '') out['reason'] = item.reason.trim();
  return out;
}

/**
 * The edit "Update {X}" sends to the row on the record: the list's dose,
 * When, directions and reason where the list holds them, the name staying
 * the record's (a look-alike is a spelling, never a rename). The prescriber
 * always travels, as the record form's edit sends it: the record's linked
 * care-team row when the list names the same person or no one, else the
 * list's words.
 */
export function updateFieldsFor(item: MedListItem, target: RecordTarget): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (item.dose.trim() !== '') out['dose'] = item.dose.trim();
  if (item.times.length > 0) out['times_of_day'] = [...item.times];
  if (item.directions.trim() !== '') out['schedule'] = item.directions.trim();
  if (item.reason.trim() !== '') out['reason'] = item.reason.trim();
  const listed = item.prescriber.trim();
  const keepRecord = listed === '' || same(listed, target.prescriber);
  if (keepRecord && target.prescriberId !== undefined)
    out['prescriber_provider_id'] = target.prescriberId;
  else if (keepRecord && (target.prescriber ?? '').trim() !== '')
    out['prescriber'] = (target.prescriber ?? '').trim();
  else if (!keepRecord) out['prescriber'] = listed;
  return out;
}
